import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { assetApi } from '@/api/asset';
import { useToast } from '@/hooks/useToast';
/**
 * Shared orchestrator hook for the Webster builder overlay.
 *
 * Manages the asset detail state and version promotion (Save as Version).
 * The type-specific chat hooks (useLandingPageChat / useEmailAssetChat) are
 * composed by the overlay component itself since they need to integrate with
 * the UI (preview panels, editors, etc.).
 */
export function useWebsterBuilder({ clientCompanyId, workspaceId, campaignId, assetId, assetType, onAssetUpdated, }) {
    const [assetDetail, setAssetDetail] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(false);
    const { showSuccess, showError, showErrorUnless422 } = useToast();
    const onAssetUpdatedRef = useRef(onAssetUpdated);
    onAssetUpdatedRef.current = onAssetUpdated;
    const loadAssetData = useCallback(async () => {
        try {
            const [detailRes, versionsRes] = await Promise.all([
                assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, assetId),
                assetApi.getAssetVersions(clientCompanyId, workspaceId, assetId),
            ]);
            if (!detailRes.error && detailRes.data)
                setAssetDetail(detailRes.data);
            if (!versionsRes.error && versionsRes.data)
                setVersions(versionsRes.data);
            return detailRes.data ?? null;
        }
        finally {
            setLoading(false);
        }
    }, [clientCompanyId, workspaceId, campaignId, assetId]);
    useEffect(() => { loadAssetData(); }, [loadAssetData]);
    const hasLandingPage = assetType === 'Landing Page' && !!assetDetail?.LandingPageId;
    const promoteDraft = useCallback(async (options) => {
        setPromoting(true);
        try {
            const response = await assetApi.promoteDraft(clientCompanyId, workspaceId, campaignId, assetId, options);
            if (response.error) {
                showErrorUnless422('Save failed', response);
                return false;
            }
            showSuccess('Version created', 'Saved as a new version for review.');
            await loadAssetData();
            onAssetUpdatedRef.current?.();
            return true;
        }
        catch {
            showError('Save failed', 'An unexpected error occurred.');
            return false;
        }
        finally {
            setPromoting(false);
        }
    }, [clientCompanyId, workspaceId, campaignId, assetId, loadAssetData, showSuccess, showError]);
    return {
        assetDetail,
        versions,
        loading,
        hasLandingPage,
        promoting,
        promoteDraft,
        refreshAsset: loadAssetData,
    };
}
